/**
 * One metric, three readings: the plan, what happened, and the re-forecast
 * that carries on from what happened.
 *
 * Form carries identity as well as colour — bars are actuals, the dashed line
 * is the plan, the dotted line the ramped plan, the solid line the re-forecast
 * — so the chart still reads with no colour at all. The palette was run through the dataviz validator for both
 * light and dark surfaces.
 */

import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell,
} from 'recharts'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui'
import { FORMATTERS, formatVariance, variance } from '../../lib/scienceModelView'

export const SERIES_COLORS = {
  actual: '#3B82F6',
  plan: '#D97706',
  reforecast: '#0D9488',
  // Closest to actual blue under deuteranopia (ΔE 6.6), which the validator
  // allows only with a second encoding: this one is always a dotted line.
  ramped: '#7C3AED',
}

const TOOLTIP_STYLE = {
  backgroundColor: 'hsl(var(--popover))',
  color: 'hsl(var(--popover-foreground))',
  border: '1px solid hsl(var(--border))',
  borderRadius: '8px',
  fontSize: 12,
}

const AXIS_TICK = { fill: 'hsl(var(--muted-foreground))', fontSize: 11 }

const DEFAULT_NAMES = {
  plan: 'Plan',
  actual: 'Actual',
  actualPartial: 'Actual to date',
  reforecast: 'Re-forecast',
  reforecastPartial: 'Re-forecast (paced)',
  ramped: 'Ramped plan',
}

function ChartTooltip({ active, payload, label, format, names, labelPrefix = '' }) {
  if (!active || !payload?.length) return null
  const point = payload[0].payload
  const full = FORMATTERS[format].full
  const reference = point.actual != null && !point.partial ? point.actual : point.reforecast
  return (
    <div style={TOOLTIP_STYLE} className="px-3 py-2 space-y-1">
      <div className="font-medium">{labelPrefix}{label}{point.partial ? ' · to date' : ''}</div>
      <Row color={SERIES_COLORS.plan} name={names.plan} value={full(point.plan)} />
      {point.ramped != null && <Row color={SERIES_COLORS.ramped} name={names.ramped} value={full(point.ramped)} />}
      {point.actual != null && <Row color={SERIES_COLORS.actual} name={point.partial ? names.actualPartial : names.actual} value={full(point.actual)} />}
      {point.reforecast != null && <Row color={SERIES_COLORS.reforecast} name={point.partial ? names.reforecastPartial : names.reforecast} value={full(point.reforecast)} />}
      {format !== 'percent' && reference != null && (
        <div className="pt-1 text-muted-foreground">vs plan {formatVariance(variance(reference, point.plan))}</div>
      )}
    </div>
  )
}

function Row({ color, name, value }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="flex items-center gap-1.5">
        <span className="inline-block h-2 w-2 rounded-sm" style={{ backgroundColor: color }} />
        {name}
      </span>
      <span className="tabular-nums">{value}</span>
    </div>
  )
}

export default function ModelChart({
  title, description, data, format = 'money', height = 260, showReforecast = true, names: nameOverrides, labelPrefix,
}) {
  const compact = FORMATTERS[format].compact
  const names = { ...DEFAULT_NAMES, ...nameOverrides }
  const showRamped = data.some(point => point.ramped != null)
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
        {description && <CardDescription className="text-xs">{description}</CardDescription>}
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={height}>
          <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" vertical={false} />
            <XAxis dataKey="label" tick={AXIS_TICK} tickLine={false} axisLine={false} interval="preserveStartEnd" />
            <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} tickFormatter={compact} width={56} />
            <Tooltip content={<ChartTooltip format={format} names={names} labelPrefix={labelPrefix} />} cursor={{ fill: 'hsl(var(--muted))', opacity: 0.4 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} iconSize={10} />
            <Bar dataKey="actual" name={names.actual} fill={SERIES_COLORS.actual} radius={[4, 4, 0, 0]} maxBarSize={28}>
              {/* The month in progress is to-date, so it is drawn lighter. */}
              {data.map(point => (
                <Cell key={point.month ?? point.date} fillOpacity={point.partial ? 0.45 : 1} />
              ))}
            </Bar>
            <Line
              type="monotone" dataKey="plan" name={names.plan} stroke={SERIES_COLORS.plan}
              strokeWidth={2} strokeDasharray="6 4" dot={false} activeDot={{ r: 4 }}
            />
            {showRamped && (
              <Line
                type="monotone" dataKey="ramped" name={names.ramped} stroke={SERIES_COLORS.ramped}
                strokeWidth={2} strokeDasharray="2 3" dot={false} activeDot={{ r: 4 }}
              />
            )}
            {showReforecast && (
              <Line
                type="monotone" dataKey="reforecast" name={names.reforecast} stroke={SERIES_COLORS.reforecast}
                strokeWidth={2} dot={false} activeDot={{ r: 4 }} connectNulls={false}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  )
}
